import type React from 'react';

import { Link } from '@change/ds-core-ui';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { Avatar } from '@change-corgi/design-system-legacy/components/media';
import { iconClose } from '@change-corgi/design-system-legacy/icons';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import type { PetitionEditPageState } from '#src/app/pages/petitionEditV2/pageContext';

import { useDecisionMakerCard } from './hooks/useDecisionMakerCard';

type DecisionMakerType = PetitionEditPageState['formState']['decisionMakers']['value'][number];

type Props = {
	decisionMaker: DecisionMakerType;
	onRemoveDecisionMaker: () => void;
	setEditing: () => void;
};

export const DecisionMakerCard: React.FC<Props> = ({ decisionMaker, onRemoveDecisionMaker, setEditing }) => {
	const { actions } = useDecisionMakerCard({ onRemoveDecisionMaker, setEditing });
	const { translate } = useI18n();

	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 8, p: 16 }}>
			<Avatar alt="avatar" size="s" src={decisionMaker.photoUrl} margin={4} />
			<Flex margin={4} sx={{ flexDirection: 'column', lineHeight: '1', flexGrow: 1 }}>
				<Text sx={{ fontWeight: 'bolder' }}>{decisionMaker.displayName}</Text>
				<Text color="neutral-grey600" sx={{ fontWeight: 'normal' }}>
					{decisionMaker.title}
				</Text>
			</Flex>
			{!decisionMaker.isConfirmed && (
				<Link
					onClick={actions.onEditDM}
					data-qa="edit-decision-maker"
					data-testid="edit-decision-maker"
					aria-label={translate('corgi.petition.edit.petition_data.decision_maker.edit')}
				>
					<Translate value="corgi.petition.edit.petition_data.decision_maker.edit" />
				</Link>
			)}
			<Button
				icon={iconClose}
				iconPosition="start"
				mode="icon"
				onClick={actions.onRemoveDM}
				size="medium"
				name="removeDm"
				data-testid="remove-decision-maker"
				data-qa="remove-decision-maker"
				sx={{
					border: 'none',
					width: 'auto',
				}}
				variant="secondary"
			>
				<Translate value="corgi.petition.edit.petition_data.decision_maker.remove" />
			</Button>
		</Box>
	);
};
